import { Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * One row of labelled fields in a detail page's header band or tab cards. Wraps only
 * when it has to, so a card's fields run across the page's width instead of down a
 * column of it.
 */
export function FieldRow({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={5} rowGap={1.5}>
      {children}
    </Stack>
  );
}

/**
 * A field's value, or a skeleton of one while the read behind it is in flight. `loaded`
 * is per-row rather than per-page: a row can come from a second read that cannot start
 * until the first has named what to ask for.
 */
export function FieldValue({
  loaded,
  value,
  fallback = '—',
  skeletonWidth = 90,
}: {
  loaded: boolean;
  value: string | number | null | undefined;
  fallback?: string;
  skeletonWidth?: number;
}) {
  if (!loaded) {
    return <Skeleton width={skeletonWidth} animation="wave" />;
  }

  // `0` is a real KPI value — only null/undefined/'' fall back to the dash.
  const text = value === null || value === undefined || value === '' ? fallback : value;
  return <Typography variant="body2">{text}</Typography>;
}
